import React from 'react';

// Soft-chip Badge + Tag (handoff bundle's core/Badge + core/Tag on app tokens).
// Badges are status pills; Tags label content and can be selectable/removable.

export type Tone =
  | 'neutral'
  | 'brand'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

// Semantic chips keep light washes in both themes (matches the app's current
// alert treatment; the highlighter accent deliberately stays yellow). text-ink
// is a fixed dark so marker chips stay readable in dark mode.
const TONE: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  brand: 'bg-blue-50 text-blue-700',
  accent: 'bg-marker-soft text-ink',
  success: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-sky-100 text-sky-800',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  /** Leading 6px status dot in the tone color. */
  dot?: boolean;
  icon?: React.ReactNode;
}

export function Badge({
  tone = 'neutral',
  dot = false,
  icon,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        TONE[tone],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {icon}
      {children}
    </span>
  );
}

export interface TagProps {
  /** JetBrains Mono — exam codes, counts. */
  mono?: boolean;
  /** Selected filter state: green wash + green border. */
  selected?: boolean;
  /** Leading color swatch dot (any CSS color). */
  color?: string;
  icon?: React.ReactNode;
  onRemove?: () => void;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  title?: string;
}

export function Tag({
  mono = false,
  selected = false,
  color,
  icon,
  onRemove,
  onClick,
  className,
  children,
  title,
}: TagProps) {
  const classes = [
    'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-xs',
    selected
      ? 'border-blue-300 bg-blue-50 text-blue-700'
      : 'border-slate-200 bg-slate-100 text-slate-700',
    onClick ? 'cursor-pointer transition hover:bg-slate-200' : '',
    mono ? 'font-mono' : '',
    className ?? '',
  ].join(' ');

  const body = (
    <>
      {color && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      {icon}
      {children}
      {onRemove && (
        <span
          role="button"
          aria-label="Remove"
          tabIndex={0}
          className="-mr-0.5 ml-0.5 rounded-full px-0.5 leading-none hover:bg-slate-300"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              onRemove();
            }
          }}
        >
          ×
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick} title={title}>
        {body}
      </button>
    );
  }
  return (
    <span className={classes} title={title}>
      {body}
    </span>
  );
}
